import { create } from 'zustand';
import type { ColumnAnalysisRequest, ColumnFloor } from './types';
import type { SeismicInput } from '../beams/types';

interface State {
  request: ColumnAnalysisRequest;
  setGeometry: (patch: Partial<ColumnAnalysisRequest['geometry']>) => void;
  setAutoLoads: (patch: Partial<ColumnAnalysisRequest['auto_loads']>) => void;
  addFloor: () => void;
  updateFloor: (idx: number, patch: Partial<ColumnFloor>) => void;
  removeFloor: (idx: number) => void;
  toggleSeismic: (on: boolean) => void;
  setSeismic: (patch: Partial<SeismicInput>) => void;
  setMaterials: (patch: Partial<Pick<ColumnAnalysisRequest, 'fc' | 'fy' | 'cover_cm' | 'combo'>>) => void;
  loadRequest: (r: ColumnAnalysisRequest) => void;
}

const defaultSeismic: SeismicInput = {
  zone: 'Z4',
  usage: 'C',
  soil: 'S2',
  system: 'porticos_concreto',
  building_height_m: 12.0,
  seismic_weight_kgf: 500_000,
  floor_shear_fraction: 1.0,
  beam_shear_fraction: 0.20,
};

const defaults: ColumnAnalysisRequest = {
  geometry: { L1_cm: 40, L2_cm: 40, H_clear_cm: 280 },
  auto_loads: {
    tributary_area_m2: 15,
    floors: [
      { use: 'vivienda', height_m: 2.8 },
      { use: 'vivienda', height_m: 2.8 },
      { use: 'vivienda', height_m: 2.8 },
    ],
    slab_dead_load_kgf_m2: 500,
  },
  seismic: null,
  combo: 'E060',
  fc: 210,
  fy: 4200,
  cover_cm: 4,
};

export const useColumnAnalysisStore = create<State>((set) => ({
  request: defaults,
  setGeometry: (patch) =>
    set((s) => ({ request: { ...s.request, geometry: { ...s.request.geometry, ...patch } } })),
  setAutoLoads: (patch) =>
    set((s) => ({ request: { ...s.request, auto_loads: { ...s.request.auto_loads, ...patch } } })),
  addFloor: () =>
    set((s) => ({
      request: {
        ...s.request,
        auto_loads: {
          ...s.request.auto_loads,
          floors: [...s.request.auto_loads.floors, { use: 'vivienda', height_m: 2.8 }],
        },
      },
    })),
  updateFloor: (idx, patch) =>
    set((s) => ({
      request: {
        ...s.request,
        auto_loads: {
          ...s.request.auto_loads,
          floors: s.request.auto_loads.floors.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
        },
      },
    })),
  removeFloor: (idx) =>
    set((s) => ({
      request: {
        ...s.request,
        auto_loads: {
          ...s.request.auto_loads,
          floors: s.request.auto_loads.floors.filter((_, i) => i !== idx),
        },
      },
    })),
  toggleSeismic: (on) =>
    set((s) => ({
      request: {
        ...s.request,
        seismic: on ? s.request.seismic ?? { ...defaultSeismic } : null,
      },
    })),
  setSeismic: (patch) =>
    set((s) => ({
      request: {
        ...s.request,
        seismic: s.request.seismic
          ? { ...s.request.seismic, ...patch }
          : { ...defaultSeismic, ...patch },
      },
    })),
  setMaterials: (patch) => set((s) => ({ request: { ...s.request, ...patch } })),
  loadRequest: (r) => set({ request: r }),
}));
